import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail, trackEvent } from "@/lib/api";
import { CONTACT_INTERESTS } from "@/lib/site";
import { useLang } from "@/context/LanguageContext";
import { CheckCircle2 } from "lucide-react";

const INTERESTS_AR = [
  "فتح حساب تداول", "معلومات عن الوسطاء", "دعم العملاء الحاليين", "الشراكة / برنامج IB",
  "شراكة تسويقية", "شكوى", "استفسار عام", "أخرى",
];

export function ContactForm() {
  const { L, isAr } = useLang();
  const F = L.forms;
  const interests = isAr ? INTERESTS_AR : CONTACT_INTERESTS;
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", country: "", subject: "",
    interest: CONTACT_INTERESTS[0], message: "", consent: false, honeypot: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error(F.consentError);
    setLoading(true);
    try {
      await api.post("/contact", form);
      trackEvent("contact_submit", { interest: form.interest });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <div className="rounded-xl border border-hair bg-white p-10 text-center shadow-sm" data-testid="contact-success">
        <CheckCircle2 className="mx-auto h-12 w-12 text-success" />
        <h3 className="mt-4 font-heading text-2xl font-semibold text-ink">{F.contactSuccessTitle}</h3>
        <p className="mt-2 text-subtle">{F.contactSuccessMsg}</p>
      </div>
    );

  return (
    <form onSubmit={submit} className="space-y-5 rounded-xl border border-hair bg-white p-6 lg:p-8 shadow-sm" data-testid="contact-form">
      <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={form.honeypot} onChange={(e) => set("honeypot", e.target.value)} />
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label={F.fullName} required><Input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="contact-name" /></Field>
        <Field label={F.email} required><Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" /></Field>
        <Field label={F.phone}><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone" /></Field>
        <Field label={F.country}><Input value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="contact-country" /></Field>
      </div>
      <Field label={F.subject}><Input value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="contact-subject" /></Field>
      <Field label={F.interest} required>
        <Select value={form.interest} onValueChange={(v) => set("interest", v)}>
          <SelectTrigger data-testid="contact-interest"><SelectValue /></SelectTrigger>
          <SelectContent>{CONTACT_INTERESTS.map((c, i) => <SelectItem key={c} value={c}>{interests[i]}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label={F.message} required><Textarea required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" /></Field>
      <label className="flex items-start gap-3 text-sm text-subtle">
        <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} data-testid="contact-consent" className="mt-0.5" />
        <span>{F.contactConsent}</span>
      </label>
      <Button type="submit" disabled={loading} className="w-full bg-finblue hover:bg-finblueDark" data-testid="contact-submit">
        {loading ? F.sending : F.sendMessage}
      </Button>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-ink">{label}{required && <span className="text-danger"> *</span>}</Label>
      {children}
    </div>
  );
}
